import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { CoverageEventPayload, RotationEventPayload, RotationPhasePayload } from '@/hooks/useWebSocket';
import { EMPTY_HANDOVER_LINKS, type HandoverLink } from '@/components/dashboard/handoverLink';

/**
 * Live coverage state for the COP's mission queue (#249).
 *
 * Same two-writer model as `useCopMissionsQueue` beside it — `coverage_event` frames are the
 * mechanism, the poll is the backstop for a socket that dropped without the browser noticing.
 * The poll matters more here than for the mission queue: a station can be held for an hour
 * without a single transition, so an operator opening the COP mid-run has no frame to learn
 * from and would see nothing at all without the initial read.
 */
const COVERAGE_REFRESH_MS = 20_000;
const COVERAGE_REQUEST_TIMEOUT_MS = 8_000;

/**
 * How long a burst of rotation frames collapses into one re-read - `useCopRotation`'s figure, for
 * its reason: one approved swap broadcasts a run of phase frames within a few seconds.
 */
const FRAME_COALESCE_MS = 400;

/**
 * The handover in progress on a station, exactly as `GET /api/coverage` sends it
 * (`CoverageHandoverSummary` in `src/services/coverage.ts`). Null on the row when none is.
 */
export interface CoverageHandover {
  action_id: string;
  phase: HandoverLink['phase'];
  phase_reason: RotationPhasePayload['phase_reason'];
  relief_drone_id: string;
  outgoing_drone_id: string;
  phase_since: string;
  cruise_speed_ms: number | null;
  /**
   * #703's last-resort recall has fired on this action: the outgoing was sent home with nobody
   * approving it, and the station is empty until the relief takes it. Separate from `phase_reason`,
   * which the next phase move clears.
   */
  outgoing_recalled: boolean;
}

export interface CoverageSummary {
  mission_id: string;
  state: 'pending_station' | 'covered' | 'handover' | 'gap' | 'suspended' | 'stopped';
  covered_by_drone_id: string | null;
  covered_by_callsign: string | null;
  coverage_target_pct: number;
  window_start: string | null;
  window_end: string | null;
  member_count: number;
  updated_at: string;
  handover: CoverageHandover | null;
}

/**
 * Every handover in progress, as the map draws it.
 *
 * ANYTHING ABNORMAL IS ONE STATE, `attention` (D17): a hold or fault the orchestrator named, a
 * last-resort recall (whether or not it succeeded - the line STAYS, because a line that disappears
 * reads as "the swap happened" while the station is empty), or a coverage read that failed and has not
 * recovered (the lines stay as last read, because vanishing reads as done, but nothing on them is
 * claimed current). The Coverage panel names which.
 *
 * A row from a server that predates the field carries no `handover` at all, and one whose timestamp
 * does not parse has nothing to count from - both are simply not drawn.
 */
export function handoverLinksFrom(
  byMission: ReadonlyMap<string, CoverageSummary>,
  degraded = false,
): HandoverLink[] {
  const links: HandoverLink[] = [];
  for (const row of byMission.values()) {
    const h = row.handover;
    if (!h) continue;
    const phaseSinceMs = Date.parse(h.phase_since);
    if (!Number.isFinite(phaseSinceMs)) continue;
    const abnormal = degraded || h.phase_reason != null || h.outgoing_recalled === true;
    links.push({
      actionId: h.action_id,
      phase: h.phase,
      reliefDroneId: h.relief_drone_id,
      outgoingDroneId: h.outgoing_drone_id,
      phaseSinceMs,
      cruiseSpeedMs: h.cruise_speed_ms,
      attention: abnormal ? 'attention' : 'none',
    });
  }
  // The shared empty list when there is nothing, so a 20 s poll of a quiet fleet does not hand the
  // map a new array and re-run its source effect for no change.
  return links.length > 0 ? links : EMPTY_HANDOVER_LINKS;
}

export function useCopCoverage(
  subscribeCoverageEvent: (cb: (event: CoverageEventPayload) => void) => () => void,
  /**
   * Optional: without it the handover still arrives, on the poll. With it, a phase change is on the
   * map within a round trip - the frame names the phase but not when it began, and the countdown
   * needs both, so the frame is a trigger to re-read rather than a payload to apply.
   */
  subscribeRotationEvent?: (cb: (event: RotationEventPayload) => void) => () => void,
) {
  const [byMission, setByMission] = useState<Map<string, CoverageSummary>>(new Map());
  /**
   * True once a read has failed and no newer one has succeeded.
   *
   * WITHOUT THIS, "we could not ask" is pixel-identical to "nothing is covered". A row with no
   * coverage marker already means four different things — never attached, `pending_station`,
   * `suspended`/`stopped`, and (before this flag) a failed read. An operator opening the COP at
   * 02:00 while `/api/coverage` is 500ing would scan a queue with no GAP anywhere and move on,
   * having been told nothing was wrong by a board that had not managed to ask.
   */
  const [degraded, setDegraded] = useState(false);
  /** Issue order, so a slow response cannot overwrite a newer one. */
  const seqRef = useRef(0);

  const refresh = useCallback(() => {
    const seq = ++seqRef.current;
    api
      .get<{ data: CoverageSummary[] }>('/api/coverage', {
        signal: AbortSignal.timeout(COVERAGE_REQUEST_TIMEOUT_MS),
      })
      .then((r) => {
        if (seq !== seqRef.current) return;
        setByMission(new Map(r.data.map((row) => [row.mission_id, row])));
        setDegraded(false);
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        // The map is NOT cleared. Emptying it on a failed poll would blank every coverage badge
        // on the board and read as "nothing is covered". What changes instead is `degraded`, so
        // the surface can say the markers may be out of date — or, on a first load that never
        // succeeded, that it has no idea at all.
        console.warn('Failed to load coverage for COP', err);
        setDegraded(true);
      });
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, COVERAGE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => subscribeCoverageEvent((event) => {
    setByMission((prev) => {
      const existing = prev.get(event.mission_id);
      // A frame for a mission this board has never read carries no target percentage or member
      // count, so it cannot synthesise a row — but it is real news, and dropping it would leave
      // a station that changed hands looking untouched until the next poll. Merged onto what is
      // known, with the configuration fields left at their defaults until the poll fills them.
      const next = new Map(prev);
      next.set(event.mission_id, {
        mission_id: event.mission_id,
        state: event.state,
        covered_by_drone_id: event.covered_by_drone_id,
        covered_by_callsign: event.covered_by_callsign,
        coverage_target_pct: existing?.coverage_target_pct ?? 0,
        window_start: existing?.window_start ?? null,
        window_end: existing?.window_end ?? null,
        member_count: existing?.member_count ?? 0,
        updated_at: event.occurred_at,
        // Not the frame's to say: a state change carries no rotation. Kept as last read, and the
        // phase frame that ends a handover re-reads it (below).
        handover: existing?.handover ?? null,
      });
      return next;
    });
  }), [subscribeCoverageEvent]);

  useEffect(() => {
    if (!subscribeRotationEvent) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeRotationEvent((event) => {
      // Schedule, safety, relief and approval frames move no aircraft; only a phase does.
      if (event.phase === null || timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        refresh();
      }, FRAME_COALESCE_MS);
    });
    return () => {
      unsubscribe();
      if (timer !== null) clearTimeout(timer);
    };
  }, [subscribeRotationEvent, refresh]);

  return {
    coverageByMission: byMission,
    /** A read has failed and none has succeeded since — the markers may be stale or absent. */
    coverageDegraded: degraded,
    refreshCoverage: refresh,
  };
}
